import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { Print as PrintIcon, Translate as TranslateIcon, ContentCopy as CopyIcon, VisibilityOff as VisibilityOffIcon, Visibility as VisibilityIcon, DeleteOutline as DeleteIcon, Headphones as HeadphonesIcon } from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import type { StoryTimeGenerateResponse } from '@/types/storytime.types';
import { useTranslateText } from '@/hooks/useTranslation';
import { decodeHtmlEntities } from '@/utils/textUtils';
import { useGenerateStoryAudio } from '@/hooks/useStoryTimeAudio';
import { useStoryTimeListeningTracker } from '@/hooks/useStoryTimeListeningTracker';
import { fetchStoryAudioBlob } from '@/services/storytime.service';

// Font size persistence helpers
const FONT_SIZE_KEY = 'scribelate_story_font_size';
const DEFAULT_FONT_SIZE = 16;

function getStoredFontSize() {
  const stored = localStorage.getItem(FONT_SIZE_KEY);
  const parsed = stored ? parseInt(stored, 10) : NaN;
  return !isNaN(parsed) && parsed > 8 && parsed < 48 ? parsed : DEFAULT_FONT_SIZE;
}

function setStoredFontSize(size: number) {
  localStorage.setItem(FONT_SIZE_KEY, String(size));
}

// Playback speed persistence helpers
const PLAYBACK_SPEED_KEY = 'scribelate_story_playback_speed';
const DEFAULT_PLAYBACK_SPEED = 1;
const PLAYBACK_SPEEDS = [0.5, .75, 1, 1.25] as const;

function getStoredPlaybackSpeed(): number {
  const stored = localStorage.getItem(PLAYBACK_SPEED_KEY);
  const parsed = stored ? parseFloat(stored) : NaN;
  return !isNaN(parsed) && (PLAYBACK_SPEEDS as readonly number[]).includes(parsed)
    ? parsed
    : DEFAULT_PLAYBACK_SPEED;
}

function setStoredPlaybackSpeed(speed: number): void {
  localStorage.setItem(PLAYBACK_SPEED_KEY, String(speed));
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const LANGUAGE_TO_CODE: Record<string, string> = {
  japanese: 'ja',
  english: 'en',
  spanish: 'es',
  french: 'fr',
  german: 'de',
  italian: 'it',
  portuguese: 'pt',
  chinese: 'zh',
  korean: 'ko',
  russian: 'ru',
  turkish: 'tr',
};

function toLanguageCode(lang: string): string {
  const lower = lang.toLowerCase();
  if (lower.includes('-')) return lower.split('-')[0];
  return LANGUAGE_TO_CODE[lower] ?? lower;
}

function printStory(title: string, storyHtml: string, fontSize: number, hideFurigana: boolean): void {
  const w = window.open('', '_blank');
  if (!w) return;
  const safeTitle = escapeHtml(title);
  const furiganaStyle = hideFurigana ? 'ruby rt { display: none; } ruby rp { display: none; }' : '';
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
<style>
  body { font-family: sans-serif; padding: 2rem; max-width: 800px; margin: 0 auto; }
  h2 { font-size: 1.75rem; margin-bottom: 1.5rem; }
  .story { font-size: ${fontSize}px; line-height: 1.8; }
  ruby { ruby-align: center; }
  ${furiganaStyle}
  @media print { body { padding: 0; } }
</style>
</head><body><h2>${safeTitle}</h2><div class="story">${storyHtml}</div></body></html>`);
  w.document.close();
  w.focus();
  w.print();
  w.close();
}

interface StoryReaderProps {
  result: StoryTimeGenerateResponse;
  topic: string;
  onReset: () => void;
  storyLanguage?: string;
  sessionId?: string;
  onDelete?: () => void;
}

export const StoryReader = ({ result, topic, onReset, storyLanguage, sessionId, onDelete }: StoryReaderProps) => {
  const isJapanese = storyLanguage?.toLowerCase() === 'japanese';
  const regionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('common');
  const { t: tPlayer } = useTranslation('player');
  const [fontSize, setFontSize] = useState<number>(getStoredFontSize());
  const [hideFurigana, setHideFurigana] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionAnchor, setSelectionAnchor] = useState<{ top: number; left: number } | null>(null);
  const [translationResult, setTranslationResult] = useState('');
  const translateText = useTranslateText();
  const generateAudio = useGenerateStoryAudio();
  const [audioObjectUrl, setAudioObjectUrl] = useState<string | null>(null);
  const [audioFetchError, setAudioFetchError] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(getStoredPlaybackSpeed());

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    setStoredPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  useStoryTimeListeningTracker({
    audioRef,
    language: storyLanguage,
    enabled: !!audioObjectUrl && !!storyLanguage,
  });

  // <audio src> can't carry apiClient's Authorization header (it issues its
  // own browser-native GET, bypassing the axios request interceptor), so we
  // fetch the stream as a blob via apiClient and play it from an object URL.
  useEffect(() => {
    return () => {
      if (audioObjectUrl) {
        URL.revokeObjectURL(audioObjectUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioObjectUrl]);

  useEffect(() => {
    setAudioObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setAudioFetchError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (audioObjectUrl && audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioObjectUrl]);

  const handleListen = () => {
    if (!sessionId) return;
    setAudioFetchError(false);
    generateAudio.mutate(
      { sessionId, language: storyLanguage ?? '' },
      {
        onSuccess: async () => {
          try {
            const blob = await fetchStoryAudioBlob(sessionId);
            const objectUrl = URL.createObjectURL(blob);
            setAudioObjectUrl((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return objectUrl;
            });
          } catch {
            setAudioFetchError(true);
          }
        },
      }
    );
  };

  const handleFontSizeChange = (delta: number) => {
    setFontSize(prev => {
      const next = Math.max(10, Math.min(40, prev + delta));
      setStoredFontSize(next);
      return next;
    });
  };

  const handleTextSelect = (e: MouseEvent<HTMLDivElement>) => {
    const text = window.getSelection()?.toString().trim() ?? '';
    if (text && text.length > 0 && text.length <= 100) {
      setSelectedText(text);
      setSelectionAnchor({ top: e.clientY + 8, left: e.clientX });
      setTranslationResult('');
    } else {
      setSelectedText('');
      setSelectionAnchor(null);
      setTranslationResult('');
    }
  };

  const handleClosePopover = () => {
    setSelectedText('');
    setSelectionAnchor(null);
    setTranslationResult('');
    window.getSelection()?.removeAllRanges();
  };

  const handleTranslate = () => {
    if (!selectedText) return;
    const sourceLanguage = storyLanguage ? toLanguageCode(storyLanguage) : 'auto';
    const targetLanguage = toLanguageCode(navigator.language);
    translateText.mutate(
      { text: selectedText, sourceLanguage, targetLanguage },
      {
        onSuccess: (data) => setTranslationResult(decodeHtmlEntities(data.translatedText)),
        onError: () => setTranslationResult('Translation failed'),
      },
    );
  };

  const sanitizedStoryHtml = DOMPurify.sanitize(result.story_html, {
    ADD_TAGS: ['ruby', 'rt', 'rp'],
  });

  useEffect(() => {
    regionRef.current?.focus();
  }, []);

  return (
    <Box
      ref={regionRef}
      role="region"
      aria-label="Generated story"
      tabIndex={-1}
      sx={{ outline: 'none', mt: 4 }}
    >
      <Typography
        variant="h2"
        component="h2"
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
      >
        {topic}
      </Typography>
      <Paper sx={{ p: { xs: 2, md: 4 }, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => handleFontSizeChange(2)} sx={{ minWidth: 0, px: 1 }} title="Increase font size">＋</Button>
            <Button size="small" onClick={() => handleFontSizeChange(-2)} sx={{ minWidth: 0, px: 1 }} title="Decrease font size">－</Button>
          </Stack>
          {isJapanese && (
            <Tooltip title={hideFurigana ? t('showFurigana', 'Show furigana') : t('hideFurigana', 'Hide furigana')}>
              <Button
                size="small"
                onClick={() => setHideFurigana(v => !v)}
                startIcon={hideFurigana ? <VisibilityOffIcon /> : <VisibilityIcon />}
                color={hideFurigana ? 'warning' : 'inherit'}
              >
                {t('furigana', 'Furigana')}
              </Button>
            </Tooltip>
          )}
          <Button size="small" startIcon={<PrintIcon />} onClick={() => printStory(topic, sanitizedStoryHtml, fontSize, hideFurigana)}>
            {t('printStory')}
          </Button>
        </Stack>
        <Box
          sx={{
            lineHeight: 1.8,
            fontSize,
            '& ruby': { rubyAlign: 'center' },
            ...(hideFurigana && { '& ruby rt': { display: 'none' }, '& ruby rp': { display: 'none' } }),
            cursor: 'text',
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedStoryHtml }}
          onMouseUp={handleTextSelect}
        />
        <Popover
          open={!!selectedText && !!selectionAnchor}
          anchorReference="anchorPosition"
          anchorPosition={selectionAnchor ?? undefined}
          onClose={handleClosePopover}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box sx={{ p: 2, minWidth: 200, maxWidth: 280 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
              {selectedText}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: translationResult ? 1 : 0 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => {
                  navigator.clipboard.writeText(selectedText).catch(() => {});
                  handleClosePopover();
                }}
              >
                Copy
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={translateText.isPending ? <CircularProgress size={16} /> : <TranslateIcon />}
                onClick={handleTranslate}
                disabled={translateText.isPending}
              >
                {translateText.isPending ? tPlayer('translatingWord') : tPlayer('translateWord')}
              </Button>
            </Stack>
            {translationResult && (
              <Typography variant="body2" color="text.secondary">
                {translationResult}
              </Typography>
            )}
          </Box>
        </Popover>
      </Paper>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Chip label={result.difficulty_band} size="small" variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          {t('storyWordCount', { count: result.word_count })}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Button variant="outlined" onClick={onReset}>
          {t('generateAnotherStory')}
        </Button>
        {sessionId && (
          <Button
            variant="outlined"
            startIcon={generateAudio.isPending ? <CircularProgress size={16} /> : <HeadphonesIcon />}
            onClick={handleListen}
            disabled={generateAudio.isPending}
          >
            {t('listenToStory', 'Listen to this story')}
          </Button>
        )}
        {onDelete && (
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
            {t('deleteStory', 'Delete story')}
          </Button>
        )}
      </Stack>
      {sessionId && audioObjectUrl && (
        <Box sx={{ mt: 2 }}>
          <audio
            ref={audioRef}
            data-testid="storytime-audio-player"
            controls
            src={audioObjectUrl}
            style={{ width: '100%' }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {PLAYBACK_SPEEDS.map((speed) => (
              <Button
                key={speed}
                size="small"
                variant={playbackSpeed === speed ? 'contained' : 'outlined'}
                aria-pressed={playbackSpeed === speed}
                onClick={() => handleSpeedChange(speed)}
              >
                {speed}x
              </Button>
            ))}
          </Stack>
        </Box>
      )}
      {sessionId && (generateAudio.isError || audioFetchError) && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {t('storyAudioError', "Couldn't generate audio for this story. Please try again.")}
        </Typography>
      )}
    </Box>
  );
};
